import { Button, Icon } from '@iq/react-components';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { removeSiteSpecificData } from '../../../../utils';
import LayoutImportPanel from '../components/LayoutImportPanel';
import { getLayouts, notifyPageChanges } from '../../../../bundles/layouts';
import { getSites, getActiveSite } from '../../../../bundles/sites';

export default function CloseImportButton({
  activeTree,
  applyImport,
  createPage,
  exportLayout,
  setActiveTree,
  isPublic,
  pages,
  changeIsPublic,
  isContributorOrAdmin,
}) {
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importedFileName, setImportedFileName] = useState(null);
  const [selectedLayoutKey, setSelectedLayoutKey] = useState(null);
  const [importedLayout, setImportedLayout] = useState(null);
  const layouts = useSelector(getLayouts);
  const sites = useSelector(getSites);
  const { id: activeSiteId } = useSelector(getActiveSite);
  const dispatch = useDispatch();
  const Grey20 = '#cccccc'; // Background of normal button
  const StatusBlue = '#3366ff'; // Background of normal button

  const resetActivePage = () => {
    setActiveTree({});
  };

  const mapSiteLayouts = () =>
    Object.keys(layouts).reduce((acc, siteIdKey) => {
      if (siteIdKey === activeSiteId) return acc;
      const site = sites.find(({ id }) => id === siteIdKey);
      if (site) {
        acc.push({ value: siteIdKey, label: site.name });
      }
      return acc;
    }, []);

  const importLayout = async (event) => {
    const [file] = event.target.files;
    if (!file) return;
    try {
      const text = await file.text();
      const parsedLayout = JSON.parse(text);
      setImportedLayout(parsedLayout);
      setImportedFileName(file.name);
      setImportError(null);
    } catch {
      setImportError('Invalid layout file. File must contain JSON structure.');
    }
  };

  const clearImport = () => {
    setShowImportOptions(false);
    setImportedFileName(null);
    setImportedLayout(null);
    setSelectedLayoutKey(null);
    setImportError(null);
  };

  useEffect(() => {
    setImportedLayout(layouts[selectedLayoutKey]);
  }, [selectedLayoutKey]);

  const _applyImport = () => {
    if (importedLayout !== null) {
      removeSiteSpecificData(importedLayout);
    }
    applyImport(importedLayout);
    clearImport();
  };

  const handleToggle = () => {
    const pageToUpdate = pages[activeTree.page];

    if (pageToUpdate) {
      changeIsPublic(pageToUpdate);
    }

    isPublic
      ? dispatch(notifyPageChanges('privatePage'))
      : dispatch(notifyPageChanges('publicPage'));
  };

  return activeTree.page >= 0 ? (
    <>
      {isContributorOrAdmin && (
        <>
          <div className="page-content">
            <p>Public Page</p>
          </div>
          <Button design="text">
            <Icon
              icon={isPublic ? 'toggle-on' : 'toggle-off'}
              size="l"
              onClick={handleToggle}
              style={{ fill: isPublic ? StatusBlue : Grey20 }}
            />
          </Button>
        </>
      )}

      <Button
        onClick={resetActivePage}
        activity="secondary"
      >
        Close
      </Button>
    </>
  ) : (
    <>
      <div className="file-import">
        <Button
          onClick={() => setShowImportOptions(true)}
          activity="secondary"
        >
          Import
        </Button>
        {showImportOptions && (
          <LayoutImportPanel
            layouts={mapSiteLayouts()}
            selectedLayoutKey={selectedLayoutKey}
            onFileInput={importLayout}
            fileName={importedFileName}
            importError={importError}
            onLayoutSelection={setSelectedLayoutKey}
            disabled={!importedLayout}
            onCancel={clearImport}
            onApply={_applyImport}
          />
        )}
      </div>
      <Button
        onClick={exportLayout}
        activity="secondary"
        disabled={!pages || pages.length === 0}
      >
        Export
      </Button>
      <Button onClick={createPage}>Add page</Button>
    </>
  );
}
